import type {
  PreferredTimezoneQuery,
  PreferredTimezoneQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { KeyBoardEvents } from '@aurora/shared-types/community/enums';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { DropdownProps } from 'react-bootstrap';
import { Dropdown, Form, InputGroup, useClassNameMapper } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import 'react-datepicker/dist/react-datepicker.min.css';
import {
  dateToShortFormat,
  getTimeZoneShortFormat
} from '../../../helpers/forms/DateHelper/DateHelper';
import ComponentHelper from '../../../helpers/ui/ComponentHelper/ComponentHelper';
import { dropdownPopperConfig } from '../../../helpers/ui/PopperJsHelper';
import Icons from '../../../icons';
import Button from '../../common/Button/Button';
import { ButtonVariant } from '../../common/Button/enums';
import { IconColor, IconSize } from '../../common/Icon/enums';
import Icon from '../../common/Icon/Icon';
import {
  TIMEZONE_DELIMITER_KEY,
  useLanguageText
} from '../../languages/UseLanguageText/useLanguageText';
import useQueryWithTracing from '../../useQueryWithTracing';
import type { DatetimeEditor, PossibleValue } from '../types';
import localStyles from './DatetimeCustomEditor.module.css';
import timezoneListQuery from './PreferredTimezone.query.graphql';
import { useUIDSeed } from 'react-uid';

/**
 * Round off datetime object to the nearest time interval.
 *
 * @callback
 * @param minutesInterval
 * @param dateTime
 * @return rounded date time object to the nearest interval
 */
function getRoundedDatetime(minutesInterval: number = 15, dateTime: Date = new Date()): Date {
  const ms = 1000 * 60 * minutesInterval;
  return new Date(Math.round(dateTime.getTime() / ms) * ms);
}

/**
 * Focus and scroll to an element.
 *
 * @callback
 * @param htmlElement to focus and scroll to
 */
function scrollToFocus(htmlElement: HTMLElement): void {
  // center align the element in time picker and set focus on it
  htmlElement.scrollIntoView({
    behavior: 'auto',
    block: 'center',
    inline: 'center'
  });
  htmlElement.focus();
}

/**
 * Set focus on prev or next month selector.
 *
 * @callback
 */
function focusMonthSelector(showOutline: boolean, ref: React.MutableRefObject<HTMLElement>): void {
  // Focus on month selector based on if the previous month selector is available
  const previousMonth: HTMLElement = ref?.current?.querySelector(
    '.react-datepicker__navigation--previous'
  );
  if (!previousMonth?.matches('.react-datepicker__navigation--previous--disabled')) {
    previousMonth.focus({
      preventScroll: true
    });
    if (!showOutline) {
      previousMonth.classList.add('react-datepicker__navigation_outline');
    }
  } else {
    const nextMonth: HTMLElement = ref?.current?.querySelector(
      '.react-datepicker__navigation--next'
    );
    nextMonth.focus({
      preventScroll: true
    });
    if (!showOutline) {
      nextMonth.classList.add('react-datepicker__navigation_outline');
    }
  }
}

/**
 * Handle enter key event on day to match time selection with click event.
 *
 * @callback
 * @param event
 */
function handleDayKeyDown(event: KeyboardEvent): void {
  if (event.key === KeyBoardEvents.ENTER) {
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLElement).click();
  }
}

/**
 * Handle keyboard events on timezone selector.
 *
 * @callback
 * @param event
 * @param showPickerAlways
 * @param ref
 */
function handleTimezoneKeyDown(
  event: KeyboardEvent,
  ref?: React.MutableRefObject<HTMLElement>,
  showPickerAlways?: boolean
): void {
  if (event.key === KeyBoardEvents.TAB) {
    if (event.shiftKey) {
      const firstTimeListItem: HTMLElement = ref?.current?.querySelector(
        '.react-datepicker__time-list'
      );
      event.preventDefault();
      event.stopPropagation();
      scrollToFocus(firstTimeListItem.lastElementChild as HTMLElement);
    } else if (!showPickerAlways) {
      event.preventDefault();
      focusMonthSelector(true, ref);
    }
  }
}

/**
 * Handle keyboard events on time selector.
 *
 * @callback
 * @param event
 * @param ref
 */
function handleTimeKeyDown(event: KeyboardEvent, ref?: React.MutableRefObject<HTMLElement>): void {
  event.preventDefault();
  const timeList = ref?.current?.querySelector('.react-datepicker__time-list');
  const active: HTMLElement = document.activeElement as HTMLElement;

  switch (event.key) {
    // handle tab events on time list
    case KeyBoardEvents.TAB: {
      if (event.shiftKey) {
        const keyboardSelectedDay: HTMLElement = ref?.current?.querySelector(
          '.react-datepicker__day--keyboard-selected'
        );
        if (keyboardSelectedDay) {
          keyboardSelectedDay.focus();
        } else {
          const selectedDay: HTMLElement = ref?.current?.querySelector(
            '.react-datepicker__day--selected'
          );
          selectedDay.focus();
        }
      } else {
        const timezone: HTMLElement = ref?.current?.querySelector('#timezone-picker');
        timezone.focus({
          preventScroll: true
        });
      }
      break;
    }

    // handle keydown event on time list
    case KeyBoardEvents.ARROW_DOWN: {
      if (active.nextSibling) {
        scrollToFocus(active.nextSibling as HTMLElement);
      } else {
        scrollToFocus(timeList.querySelector('li'));
      }
      break;
    }

    // handle keyup event on time list
    case KeyBoardEvents.ARROW_UP: {
      if (active.previousSibling) {
        scrollToFocus(active.previousSibling as HTMLElement);
      } else {
        scrollToFocus(timeList.querySelector('li:last-child'));
      }
      break;
    }

    // handle enter event on time list
    case KeyBoardEvents.ENTER: {
      active.click();
      active.focus();
      break;
    }

    default:
  }
}

/**
 * Handle keyboard events on next month navigation.
 *
 * @param event
 * @param ref
 */
function handleTabKeyboardEvent(
  event: KeyboardEvent,
  ref?: React.MutableRefObject<HTMLElement>
): void {
  if (event.key === KeyBoardEvents.TAB && event.shiftKey) {
    const previousMonth: HTMLElement = ref?.current?.querySelector(
      '.react-datepicker__navigation--previous'
    );
    if (previousMonth.matches('.react-datepicker__navigation--previous--disabled')) {
      const timezone: HTMLElement = ref?.current?.querySelector('#timezone-picker');
      event.preventDefault();
      event.stopPropagation();
      timezone.focus({
        preventScroll: true
      });
    }
  }
}

/**
 * Handle keyboard events on month navigation.
 *
 * @param event
 */
function handleKeyboardEventPropagation(event: KeyboardEvent): void {
  event.stopPropagation();
}

/**
 * Handle allow focus highlight after the initial open state.
 * @param event
 */
function setFocusState(event: Event) {
  (event.target as HTMLElement).classList.remove('react-datepicker__navigation_outline');
}

/**
 * Set focus on clear button.
 *
 * @callback
 */
function focusClearButton(ref: React.MutableRefObject<HTMLElement>): void {
  const clearButton: HTMLElement = ref?.current?.querySelector('.lia-close-button');
  clearButton?.setAttribute('tabIndex', '0');
  clearButton?.addEventListener('keypress', handleKeyboardEventPropagation);
  clearButton?.focus();
}

interface Props extends DatetimeEditor {
  /**
   * Time interval in minutes for time selector.
   */
  timeInterval?: number;
  /**
   * Date format for date picker.
   */
  dateFormat?: string;
  /**
   * Format of the output date.
   */
  timeFormat?: string;
  /**
   * Whether to show date picker always.
   */
  showPickerAlways?: boolean;
  /**
   * Allow date picker to selects range between two dates.
   */
  useDateRange?: boolean;
  /**
   * To display the clear icon.
   */
  useClear?: boolean;
  /**
   * To disable the keyboard action
   */
  isKeyboardDisabled?: boolean;
  /**
   * Whether to show timezone picker or not.
   */
  showTimezonePicker?: boolean;
  /**
   * Whether the date picker is disabled or not.
   */
  disabled?: boolean;
  /**
   * Whether the field is in an invalid state
   */
  isInvalid?: boolean;
  /**
   * The data test id
   */
  dataTestId?: string;
}

const DatetimeCustomEditor: React.FC<React.PropsWithChildren<Props>> = ({
  useTimeSelect = true,
  value,
  minDate = new Date(),
  maxDate = new Date(),
  openToDate = new Date(),
  placeholder,
  timeInterval,
  dateFormat,
  timeFormat,
  onChange,
  disabled,
  focus,
  focusHandler,
  hasBeenFocused,
  className,
  showPickerAlways = false,
  useDateRange = false,
  useClear = true,
  isKeyboardDisabled = false,
  showTimezonePicker = false,
  isInvalid = false,
  dataTestId = 'datePicker'
}) => {
  const cx = useClassNameMapper(localStyles);
  const uidSeed = useUIDSeed();
  const uid = uidSeed('lia-datetime-custom-editor');
  const { getTimeZoneText, loading: commonTextLoading } = useLanguageText();
  const calendarRef = useRef<DatePicker>();
  const dateTimeRef = useRef<HTMLDivElement>();
  const [startDate, setStartDate] = useState(
    useDateRange ? value?.dateTime.startDate : value?.dateTime
  );
  const [endDate, setEndDate] = useState(useDateRange ? value?.dateTime.endDate : '');
  const [dateSelected, setDateSelected] = useState(false);
  const [timeSelected, setTimeSelected] = useState(false);
  const [timeZoneSelected, setTimeZoneSelected] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isFocusByMouse, setIsFocusByMouse] = useState(false);
  const dropdownDropDirection: DropdownProps['drop'] = isInvalid ? 'up' : 'down';
  let maxDateAllowed = maxDate;
  if (useDateRange && endDate === null) {
    const localDate = new Date(startDate as Date);
    const diffTime = Math.abs(maxDate.valueOf() - localDate.valueOf());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays >= 365) {
      localDate.setDate(localDate.getDate() - 1);
      maxDateAllowed = new Date(localDate.setFullYear(localDate.getFullYear() + 1));
    }
  }

  const { data, loading } = useQueryWithTracing<
    PreferredTimezoneQuery,
    PreferredTimezoneQueryVariables
  >(module, timezoneListQuery, {
    skip: !showTimezonePicker
  });

  /**
   * Once the PreferredTimezone query is complete and if the date-picker must always be shown,
   * set setShowCalendar to true
   */
  useEffect(() => {
    if (!loading && showPickerAlways) {
      setShowCalendar(true);
    }
  }, [loading, showPickerAlways]);

  const InputClickEvent = useCallback(() => {
    // Initialise calendar opened by click event.
    setIsFocusByMouse(true);
    const daySelector = dateTimeRef?.current?.querySelector('.react-datepicker__month');
    if (daySelector) {
      focusMonthSelector(false, dateTimeRef);
    }
  }, []);

  const InputKeyEvent = useCallback(() => {
    // Initialise calendar opened by key event.
    setIsFocusByMouse(false);
    const daySelector = dateTimeRef?.current?.querySelector('.react-datepicker__month');
    if (daySelector) {
      focusMonthSelector(true, dateTimeRef);
    }
  }, []);

  useEffect(() => {
    const datetimeInput = dateTimeRef.current?.querySelector('.lia-datepicker-input');
    if (datetimeInput) {
      datetimeInput.addEventListener('click', InputClickEvent);
      datetimeInput.addEventListener('keyup', InputKeyEvent);
    }
  }, [dateTimeRef, InputClickEvent, InputKeyEvent, showCalendar]);

  useEffect(() => {
    calendarRef?.current?.setOpen(true);
    const daySelector = dateTimeRef?.current?.querySelector('.react-datepicker__month');
    const timeList = dateTimeRef?.current?.querySelector('.react-datepicker__time-list');
    const clearDatetime = dateTimeRef?.current?.querySelector('.lia-close-button');
    const monthNavigationPrevious = dateTimeRef?.current?.querySelector(
      '.react-datepicker__navigation--previous'
    );
    const monthNavigationNext = dateTimeRef?.current?.querySelector(
      '.react-datepicker__navigation--next'
    );
    const timezoneSelect: HTMLElement = dateTimeRef?.current?.querySelector('#timezone-picker');
    if (showCalendar) {
      // remove the extra tab index on the time picker list
      timeList?.removeAttribute('tabIndex');
      clearDatetime?.setAttribute('tabIndex', '0');
      daySelector?.addEventListener('keydown', handleDayKeyDown);
      timeList?.addEventListener('keydown', event =>
        handleTimeKeyDown(event as KeyboardEvent, dateTimeRef)
      );
      timezoneSelect?.addEventListener('keydown', event =>
        handleTimezoneKeyDown(event, dateTimeRef, showPickerAlways)
      );
      timeList?.addEventListener('focus', event => {
        const firstTimeListItem: HTMLElement = dateTimeRef?.current?.querySelector(
          '.react-datepicker__time-list'
        );
        event.preventDefault();
        event.stopPropagation();
        scrollToFocus(firstTimeListItem.firstChild as HTMLElement);
      });

      if (monthNavigationNext) {
        monthNavigationNext.addEventListener('keydown', event =>
          handleTabKeyboardEvent(event as KeyboardEvent, dateTimeRef)
        );
        monthNavigationNext.addEventListener('keypress', handleKeyboardEventPropagation);
        monthNavigationPrevious.addEventListener('keypress', handleKeyboardEventPropagation);
        monthNavigationNext.addEventListener('blur', setFocusState);
        monthNavigationPrevious.addEventListener('blur', setFocusState);
        monthNavigationNext.addEventListener('click', setFocusState);
        monthNavigationPrevious.addEventListener('click', setFocusState);
      }
    }
    return () => {
      if (monthNavigationNext) {
        timeList?.removeEventListener('keydown', handleTimeKeyDown);
        daySelector?.removeEventListener('keydown', handleDayKeyDown);
        monthNavigationPrevious?.removeEventListener('keydown', handleTabKeyboardEvent);
        monthNavigationNext?.removeEventListener('keypress', handleKeyboardEventPropagation);
        monthNavigationNext?.removeEventListener('click', setFocusState);
        monthNavigationPrevious?.removeEventListener('click', setFocusState);
        timezoneSelect?.removeEventListener('keydown', handleTimezoneKeyDown);
      }
    };
  }, [dateTimeRef, calendarRef, showCalendar, showPickerAlways]);

  useEffect(() => {
    if (focus && calendarRef.current) {
      ComponentHelper.focusHandler(
        hasBeenFocused,
        calendarRef.current,
        focusHandler ?? (() => calendarRef.current.setFocus())
      );
    }
  }, [focus, focusHandler, calendarRef, hasBeenFocused]);

  if (commonTextLoading || loading) {
    return null;
  }
  const timeZone = data?.self.profileSettings.timeZone;
  const timezoneValue = value?.timezone || timeZone?.value;

  /**
   * Function to get the timezones to be displayed on top of the option list.
   * If the user's timezone is detected, that will be displayed, or else few other standard timezones will be displayed as in LIA.
   * @param timeZonePossibleValues possible timezone value list
   * @param preferredTimeZones user preferred timezone
   */
  function getUserPreferredTimeZones(
    timeZonePossibleValues: Array<string>,
    preferredTimeZones: Array<string>
  ): Array<PossibleValue<string, FormData>> {
    const userDetectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let newPreferredTimeZones;

    if (userDetectedTimeZone && timeZonePossibleValues.includes(userDetectedTimeZone)) {
      newPreferredTimeZones = [userDetectedTimeZone];
    } else {
      newPreferredTimeZones = preferredTimeZones;
    }

    if (newPreferredTimeZones.length > 0) {
      newPreferredTimeZones.push(TIMEZONE_DELIMITER_KEY);
    }

    return newPreferredTimeZones.map(possibleValue => {
      // we don't support text keys containing '/', see LIA-96949
      const timeZoneKeyValue = possibleValue.replace('/', '-');
      return {
        key: `${possibleValue}-preferred`,
        value: possibleValue,
        label: getTimeZoneText(timeZoneKeyValue),
        isDisabled: possibleValue === ''
      };
    });
  }

  /**
   * Function to map the possible values of the respective field
   * @param possibleValues possible values list
   */
  function getPreferencesOptions(
    possibleValues: Array<string>
  ): Array<PossibleValue<string, FormData>> {
    return possibleValues.map(possibleValue => {
      // we don't support text keys containing '/', see LIA-96949
      const timeZoneKeyValue = possibleValue.replace('/', '-');
      return {
        key: possibleValue,
        value: possibleValue,
        label: getTimeZoneText(timeZoneKeyValue)
      };
    });
  }

  /**
   * Function to get the possible values of timezone
   */
  function getPossibleTimeZoneValues() {
    const timeZonePossibleValues: Array<string> = timeZone ? [...timeZone.possibleValues] : [];
    const dividerIndex: number = timeZonePossibleValues.indexOf('');
    const preferredTimeZones: Array<string> =
      dividerIndex !== -1 ? [...timeZonePossibleValues].slice(0, dividerIndex) : [];
    const uniqueTimezones: Array<string> =
      dividerIndex !== -1
        ? [...timeZonePossibleValues].slice(dividerIndex + 1)
        : timeZonePossibleValues;

    return [
      ...getUserPreferredTimeZones(timeZonePossibleValues, preferredTimeZones),
      ...getPreferencesOptions(uniqueTimezones)
    ];
  }

  const onDateTimeSelect = (date, ref) => {
    // if calendar is expected to select two dates between range
    if (useDateRange) {
      const [startDateSelected, endDateSelected] = date;
      const roundedStartDateTime = getRoundedDatetime(timeInterval, startDateSelected);
      const roundedEndDateTime =
        endDateSelected && getRoundedDatetime(timeInterval, endDateSelected);
      setStartDate(roundedStartDateTime);
      setEndDate(roundedEndDateTime);
      onChange({
        dateTime: { startDate: roundedStartDateTime, endDate: roundedEndDateTime },
        timezone: timezoneValue
      });
      if (endDateSelected) {
        setShowCalendar(false);
      }
      return null;
    }

    // if time picker is active
    if (useTimeSelect) {
      if (dateSelected && timeSelected && timeZoneSelected) {
        setShowCalendar(false);
      }
      // if onChange event is fired and time is still not selected
      if ((dateSelected && !timeSelected) || !dateSelected) {
        setTimeSelected(true);
      }
      if (!showPickerAlways && dateSelected && !isFocusByMouse) {
        // set focus to the clear button if the picker was opened with a mouse event
        focusClearButton(ref);
      }
    } else {
      setDateSelected(true);
    }
    // on clear
    if (!date) {
      setDateSelected(false);
      setTimeSelected(false);
    }
    const roundedDateTime = date && getRoundedDatetime(timeInterval, date);
    setStartDate(roundedDateTime);
    onChange({ dateTime: roundedDateTime, timezone: timezoneValue });

    //if no time select, close the calendar modal
    if (!useTimeSelect && !showPickerAlways) {
      setShowCalendar(false);
      return null;
    }
  };

  /**
   * Handle click outside picker - special handling when we want picker to be shown always.
   *
   */
  function onClickOutsideHandler(event: MouseEvent) {
    if (dateTimeRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    if (showPickerAlways) {
      setShowCalendar(true);
    } else {
      setShowCalendar(false);
    }
  }

  /**
   * This function handles the toggle event of the dropdown
   * @param _isOpen
   * @param event dropdown toggle event
   */
  const onToggle = (_isOpen, event: React.SyntheticEvent<Dropdown>) => {
    if (!(event instanceof Event)) {
      event.stopPropagation();
      calendarRef.current.setOpen(true);
      setShowCalendar(true);
    } else if (
      !showPickerAlways &&
      ((event instanceof KeyboardEvent && event.key !== KeyBoardEvents.TAB) ||
        event instanceof MouseEvent)
    ) {
      focusClearButton(dateTimeRef);
      setShowCalendar(false);
      calendarRef.current.setOpen(false);
    }
  };

  /**
   * This function display the value in input field
   */
  function getDisplayValue() {
    if (!startDate) {
      return '';
    }
    const selectedTimezone = getPossibleTimeZoneValues().find(
      timezone => timezone.key === timezoneValue
    );

    const timeToDisplay =
      useTimeSelect &&
      (startDate as Date)?.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    if (useDateRange) {
      const startDateToDisplay = dateToShortFormat(startDate as Date);
      return endDate !== null
        ? `${startDateToDisplay} - ${dateToShortFormat(endDate as Date)}`
        : startDateToDisplay;
    }

    if (!useTimeSelect) {
      return startDate ? `${dateToShortFormat(startDate as Date)}` : '';
    }

    const timezoneToDisplay =
      showTimezonePicker && selectedTimezone
        ? `, ${getTimeZoneShortFormat(new Date(startDate as Date), selectedTimezone.key)}`
        : '';

    return startDate
      ? `${dateToShortFormat(startDate as Date)} ${timeToDisplay}${timezoneToDisplay}`
      : null;
  }

  /**
   *  Function to render the timezone picker
   */
  function renderTimezonePicker() {
    return (
      <Form.Group
        controlId={'timezone-picker'}
        data-testid="timezonePicker"
        className={cx('lia-timezone-select', {
          'lia-timezone-select-stick': showPickerAlways
        })}
      >
        <Form.Label>{getTimeZoneText('fieldLabel')}</Form.Label>
        <Form.Control
          as="select"
          onChange={event => {
            onChange({ dateTime: value?.dateTime, timezone: event.target.value });
            setTimeZoneSelected(true);
            if (value?.dateTime) {
              setShowCalendar(false);
              focusClearButton(dateTimeRef);
            }
          }}
          value={timezoneValue}
        >
          {getPossibleTimeZoneValues().map(option => {
            const { label, key, isDisabled, value: optionValue } = option;
            return (
              <option key={key} value={optionValue} disabled={isDisabled}>
                {label}
              </option>
            );
          })}
        </Form.Control>
      </Form.Group>
    );
  }

  /**
   * Handle close action
   *
   * @callback
   * @param event
   */
  function handleCloseAction(event): void {
    event.preventDefault();
    event.stopPropagation();
    setStartDate(null);
    setShowCalendar(false);
    setTimeSelected(false);
    setTimeZoneSelected(false);
    calendarRef.current.setOpen(false);
    onDateTimeSelect(null, dateTimeRef);
    onChange(null);
  }

  return (
    <div ref={dateTimeRef}>
      <Dropdown
        className={cx(className)}
        show={showPickerAlways || showCalendar}
        onToggle={onToggle}
        drop={dropdownDropDirection}
      >
        <Dropdown.Toggle as="div" id={uid} role="dialog">
          <InputGroup className={cx('lia-datepicker-input-group')}>
            <Form.Control
              as="input"
              type="text"
              className={cx('lia-datepicker-input lia-datepicker-input-field')}
              placeholder={placeholder}
              autoComplete="off"
              disabled={disabled}
              value={getDisplayValue()}
              onFocus={() => {
                setShowCalendar(true);
              }}
              readOnly
              data-testid="datePicker-Dropdown"
            />
            {startDate && !showPickerAlways && !useDateRange && useClear && (
              <InputGroup.Append>
                <Button
                  className={cx('lia-close-button')}
                  variant={ButtonVariant.NO_VARIANT}
                  onClick={handleCloseAction}
                  aria-label="Clear publication schedule"
                  data-testid="datetimePicker.Close"
                >
                  <Icon size={IconSize.PX_12} color={IconColor.GRAY_900} icon={Icons.CloseIcon} />
                </Button>
              </InputGroup.Append>
            )}
          </InputGroup>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className={cx('lia-datepicker-dropdown-menu', {
            'lia-datepicker-dropdown-menu-open': showPickerAlways,
            'lia-datepicker-dropdown-menu-with-time': useTimeSelect
          })}
          aria-labelledby={uid}
          popperConfig={dropdownPopperConfig}
          renderOnMount
          data-testid={dataTestId}
        >
          <DatePicker
            ariaInvalid="true"
            isClearable={!showPickerAlways && useClear}
            fixedHeight
            autoComplete="off"
            showDisabledMonthNavigation
            wrapperClassName={cx('lia-datepicker', className)}
            calendarClassName={cx(
              { 'lia-month-select-next': useDateRange || (!showPickerAlways && !useTimeSelect) },
              { 'lia-datepicker-popup-no-shadow': showPickerAlways },
              'lia-datepicker-popup',
              { 'lia-datepicker-time-list': !showPickerAlways && useTimeSelect }
            )}
            popperClassName={cx(
              { 'lia-datpicker-selector-no-transform': showPickerAlways },
              'lia-datepicker-selector'
            )}
            onClickOutside={onClickOutsideHandler}
            placeholderText={placeholder}
            ref={calendarRef}
            openToDate={startDate || new Date(openToDate.getTime() + timeInterval * 2 * 60 * 1000)}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            timeIntervals={timeInterval}
            showTimeSelect={useTimeSelect}
            minDate={minDate.setHours(0, 0, 0, 0)}
            maxDate={maxDateAllowed.setHours(0, 0, 0, 0)}
            selectsRange={useDateRange}
            shouldCloseOnSelect={false}
            selected={startDate}
            startDate={startDate}
            endDate={useDateRange && endDate}
            onChange={date => {
              onDateTimeSelect(date, dateTimeRef);
            }}
            onSelect={() => {
              // is fired on selecting date only
              setDateSelected(true);
              if (useTimeSelect && timeSelected && !showPickerAlways && !isFocusByMouse) {
                // set focus to the clear button if the picker was opened with a mouse event
                focusClearButton(dateTimeRef);
              }
            }}
            onChangeRaw={event => event.preventDefault()}
            onCalendarOpen={() => {
              if (useDateRange) {
                setTimeSelected(false);
              }
              if (!showCalendar && !showPickerAlways) {
                setTimeSelected(false);
              }
              if (!value?.timezone && showTimezonePicker && value?.dateTime && !useDateRange) {
                onChange({ dateTime: value?.dateTime, timezone: timezoneValue });
              }
            }}
            onCalendarClose={() => {
              setIsFocusByMouse(false);
              if (showPickerAlways || showCalendar) {
                setShowCalendar(true);
              } else {
                setShowCalendar(false);
                calendarRef.current.setOpen(false);
              }
            }}
            disabledKeyboardNavigation={isKeyboardDisabled}
            inline
          />
          {showTimezonePicker && renderTimezonePicker()}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default DatetimeCustomEditor;
